// Backend Role interface
export interface BackendRole {
  id: number;
  name: string;
}

// User Types
export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHERS = 'OTHERS',
}

export interface User {
  id: string | number;
  username?: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  gender?: Gender | null;
  role: UserRole; // Frontend mapped role
  roles?: BackendRole[]; // Backend roles array
  enabled?: boolean;
  isActive?: boolean;
  emailVerified?: boolean;
  accountLocked?: boolean;
  requirePasswordChange?: boolean;
  createdAt: string;
  updatedAt: string;
  phone?: string;
  phoneNumber?: string | null;
  profileImage?: string;
  profilePicture?: string | null;
  postcode?: string | null;
  houseNumber?: string | null;
  authTokenResponse: AuthTokenResponse;
  assignedTeacherId?: string | null;
  assignedTeacherUsername?: string | null;
  examDate?: string | null;
}

export interface AuthTokenResponse {
    accessToken: string;
    refreshToken: string;
    tokenType: string;
    roles: string;
    expiresAt: string;
}

export enum UserRole {
  STUDENT = 'student',
  TEACHER = 'teacher',
  ADMIN = 'admin'
}

export interface Student extends User {
  role: UserRole.STUDENT;
  assignedTeacher?: string; // Teacher ID
  dateOfBirth?: string;
  postcode?: string;
  emergencyContact?: string;
  licenseType?: LicenseType;
  examNotes?: string | null;
  examResult?: string | null;
}

export interface Teacher extends User {
  role: UserRole.TEACHER;
  experience?: number;
  maxLessonsPerStudent?: number;
  availability?: TimeSlot[];
  teacherLicenses?: string[];
}

export interface Admin extends User {
  role: UserRole.ADMIN;
  permissions?: AdminPermission[];
}

// Lesson Types
export interface Lesson {
  id: string;
  studentId: string;
  teacherId: string;
  dateTime: string;
  startTime?: string; // optional HH:mm derived on server for convenience
  duration: number; // in minutes
  // Backend may return upper-case status strings (e.g., "SCHEDULED", "AVAILABLE").
  // Keep as string to tolerate both enum values and raw server strings.
  status: string;
  type: LessonType | string;
  location?: string;
  // Optional display names from DTO (no nested user objects)
  studentName?: string | null;
  studentFirstName?: string | null;
  teacherName?: string | null;
  // Legacy single note field from older backend versions
  notes?: string;
  // New separate notes
  teacherNote?: string;
  studentNote?: string;
  videoUrl?: string;
  filmingConsent?: boolean;
  examType?: string;
  licenseType?: string;
  examResult?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export enum LessonStatus {
  SCHEDULED = 'scheduled',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  IN_PROGRESS = 'in_progress'
}

export enum LessonType {
  THEORY = 'theory',
  PRACTICAL = 'practical',
  EXAM_PREP = 'exam_prep',
  EXAM = 'exam'
}

export enum LicenseType {
  AM = 'AM',
  A1 = 'A1',
  A2 = 'A2',
  A = 'A',
  B_MANUAL = 'B_MANUAL',
  B_AUTOMATIC = 'B_AUTOMATIC',
  BE = 'BE',
  B_PLUS = 'B+',
  C1 = 'C1',
  C1E = 'C1E',
  C = 'C',
  CE = 'CE',
  D1 = 'D1',
  D1E = 'D1E',
  D = 'D',
  DE = 'DE',
  T = 'T'
}

// Payment Types
export interface Payment {
  id: string;
  studentId: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  method: PaymentMethod;
  description?: string;
  proofDocument?: string; // URL to uploaded PDF
  createdAt: string;
  verifiedAt?: string;
  verifiedBy?: string; // Admin/Teacher ID
}

export enum PaymentStatus {
  PENDING = 'pending',
  VERIFIED = 'verified',
  REJECTED = 'rejected'
}

export enum PaymentMethod {
  CASH = 'cash',
  BANK_TRANSFER = 'bank_transfer',
  CARD = 'card',
  OTHER = 'other'
}

export interface ExamPaymentDetail {
  id: number;
  type: string;
  startTime: string;
  location: string;
  cost: number;
  currency: string;
  studentRating?: number | null;
}

export interface LessonPaymentDetail {
  id: number;
  type: string;
  startTime: string;
  duration: number;
  cost: number;
  currency: string;
  studentRating?: number | null;
}

export interface StudentPaymentDetails {
  studentId: number;
  studentName: string;
  totalPaid: number;
  totalPending: number;
  totalDue: number;
  currency: string;
  paidPayments: Payment[];
  pendingPayments: Payment[];
  exams: ExamPaymentDetail[];
  lessons: LessonPaymentDetail[];
}

// Time Slot Types
export interface TimeSlot {
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  startTime: string; // "HH:MM" format
  endTime: string; // "HH:MM" format
}

// Admin Permission Types
export enum AdminPermission {
  MANAGE_USERS = 'manage_users',
  MANAGE_LESSONS = 'manage_lessons',
  MANAGE_PAYMENTS = 'manage_payments',
  VIEW_REPORTS = 'view_reports',
  SYSTEM_SETTINGS = 'system_settings'
}

// Authentication Types
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface SignupCredentials {
  houseNumber: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  drivingSchoolName: string;
  postcode: string;
  roles?: string[];
  teacherLicenses: string[];
  interests?: string[];
  studentLicenseType?: string;
  recaptchaToken?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  logoutSuccess: boolean;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Dashboard Data Types
export interface StudentDashboardData {
  upcomingLessons: Lesson[];
  pastLessons: Lesson[];
  pendingPayments: Payment[];
  profile: Student;
}

export interface TeacherDashboardData {
  assignedStudents: Student[];
  upcomingLessons: Lesson[];
  nextMonthLessons: Lesson[];
  profile: Teacher;
}

export interface AdminDashboardData {
  allStudents: Student[];
  allTeachers: Teacher[];
  allPayments: Payment[];
  recentLessons: Lesson[];
  systemStats: {
    totalStudents: number;
    totalTeachers: number;
    totalLessons: number;
    pendingPayments: number;
  };
}